import {
  Match,
  Team,
  SetRecord,
  Rally,
  Timeout,
  TournamentRules,
  MatchStatus,
  RotationState,
  TeamState,
  SetState,
  MatchState,
  MatchRulesConfig,
  getMatchRules,
} from '@/types';
import {
  checkSetWin,
  checkMatchWin,
  isGoldenPointMode,
  getFirstServingTeam,
} from '@/lib/validation';

export interface DbServingState {
  servingTeamId: string | null;
  serverId: string | null;
}

export interface MatchStateInput {
  match: Match;
  teamA: Team;
  teamB: Team;
  sets: SetRecord[];
  currentSetRallies: Rally[];
  tournamentRules: TournamentRules | null;
  timeouts: Timeout[];
  dbServingState: DbServingState;
  activeTimeout: Timeout | null;
}

export interface ComputedSetState {
  setNumber: number;
  setId: string;
  scoreA: number;
  scoreB: number;
  servingTeamId: string;
  currentServerId: string;
  goldenPoint: boolean;
  setWinnerId: string | null;
  rallyCount: number;
  endedAt: string | null;
  rallies: Rally[];
}

export interface ComputedMatchState {
  status: MatchStatus;
  currentSetNumber: number;
  currentSet: ComputedSetState | null;
  setsWonA: number;
  setsWonB: number;
  matchWinnerId: string | null;
  teamA: TeamState;
  teamB: TeamState;
  sets: SetRecord[];
  timeoutCountA: number;
  timeoutCountB: number;
  activeTimeout: Timeout | null;
  sideChangeDue: boolean;
  matchRules: MatchRulesConfig;
}

interface DerivedServingState {
  servingTeamId: string;
  currentServerId: string;
}

function resolveRules(match: Match, tournamentRules: TournamentRules | null): MatchRulesConfig {
  return getMatchRules(
    match.matchType,
    tournamentRules,
    match.winningPoint,
    match.goldenPoint,
  );
}

function findCurrentSetRecord(sets: SetRecord[]): SetRecord | null {
  const inProgress = sets.find(s => s.endedAt === null);
  if (inProgress) return inProgress;
  if (sets.length === 0) return null;
  return sets[sets.length - 1];
}

function deriveServingState(
  rallies: Rally[],
  dbServingState: DbServingState,
  setNumber: number,
  match: Match,
  sets: SetRecord[],
): DerivedServingState {
  const sortedAsc = [...rallies].sort((a, b) => a.rallyNumber - b.rallyNumber);

  if (sortedAsc.length === 0) {
    if (dbServingState.servingTeamId) {
      return {
        servingTeamId: dbServingState.servingTeamId,
        currentServerId: dbServingState.serverId ?? '',
      };
    }
    if (setNumber === 1) {
      const teamId = getFirstServingTeam(
        match.tossWonById,
        match.tossChoice,
        match.teamAId,
        match.teamBId,
      );
      return { servingTeamId: teamId, currentServerId: '' };
    }
    const prevSet = sets.find(s => s.setNumber === setNumber - 1);
    if (prevSet && prevSet.winnerId) {
      const losingTeamId = prevSet.winnerId === match.teamAId ? match.teamBId : match.teamAId;
      return { servingTeamId: losingTeamId, currentServerId: '' };
    }
    return { servingTeamId: match.teamAId, currentServerId: '' };
  }

  const lastRally = sortedAsc[sortedAsc.length - 1];
  const servingTeamWon = lastRally.pointWonById === lastRally.servingTeamId;

  if (servingTeamWon) {
    return {
      servingTeamId: lastRally.servingTeamId,
      currentServerId: lastRally.serverId,
    };
  }

  const newServingTeamId = lastRally.pointWonById;
  const dbHasNewServer =
    dbServingState.servingTeamId === newServingTeamId && dbServingState.serverId;
  return {
    servingTeamId: newServingTeamId,
    currentServerId: dbHasNewServer ? dbServingState.serverId! : '',
  };
}

function rebuildRotation(
  teamId: string,
  rallies: Rally[],
  initialServerId: string | null,
): RotationState {
  const serveHistory: string[] = [];
  const cycleServed: string[] = [];

  if (initialServerId) {
    serveHistory.push(initialServerId);
    cycleServed.push(initialServerId);
  }

  const teamServeRallies = rallies
    .filter(
      r =>
        r.servingTeamId === teamId &&
        r.lostReason !== 'FOUL' &&
        !r.isServerFault,
    )
    .sort((a, b) => a.rallyNumber - b.rallyNumber);

  for (const rally of teamServeRallies) {
    const lastEntry = serveHistory[serveHistory.length - 1];
    if (rally.serverId !== lastEntry) {
      serveHistory.push(rally.serverId);
      if (!cycleServed.includes(rally.serverId)) {
        cycleServed.push(rally.serverId);
      }
    }
  }

  return { serveHistory, cycleServed };
}

function deriveSideChange(
  scoreA: number,
  scoreB: number,
  rules: MatchRulesConfig,
  currentSetNumber: number,
  sideChangeShown: boolean,
): boolean {
  const isDecidingSet = currentSetNumber === rules.maxSets;
  if (!isDecidingSet) return false;
  const midpoint = Math.ceil(rules.winningPoint / 2);
  const reached = scoreA >= midpoint && scoreB >= midpoint;
  return reached && !sideChangeShown;
}

function countTimeouts(timeouts: Timeout[], teamId: string): number {
  return timeouts.filter(t => t.teamId === teamId && t.endedAt !== null).length;
}

function buildTeamState(
  team: Team,
  setsWon: number,
  rotationState: RotationState,
): TeamState {
  return {
    id: team.id,
    name: team.name,
    shortName: team.shortName,
    color: team.color,
    logoUrl: team.logoUrl,
    players: team.players,
    setsWon,
    rotationState,
  };
}

export function recomputeMatchState(input: MatchStateInput): ComputedMatchState {
  const { match, teamA, teamB, sets, timeouts, activeTimeout, tournamentRules } = input;
  const matchRules = resolveRules(match, tournamentRules ?? null);

  const currentSetRecord = findCurrentSetRecord(sets);
  const currentSetNumber = currentSetRecord?.setNumber ?? 0;
  const rallies = input.currentSetRallies;

  const scoreA = rallies.filter(r => r.pointWonById === teamA.id).length;
  const scoreB = rallies.filter(r => r.pointWonById === teamB.id).length;

  const { servingTeamId, currentServerId } = deriveServingState(
    rallies,
    input.dbServingState,
    currentSetNumber,
    match,
    sets,
  );

  const setWinResult = checkSetWin(scoreA, scoreB, matchRules);
  const setWinnerId = setWinResult.winner
    ? setWinResult.winner === 'A'
      ? teamA.id
      : teamB.id
    : null;

  const updatedSets = currentSetRecord
    ? sets.map(s =>
        s.id === currentSetRecord.id
          ? {
              ...s,
              scoreA,
              scoreB,
              winnerId: setWinnerId,
              endedAt: setWinnerId ? s.endedAt ?? new Date().toISOString() : null,
            }
          : s,
      )
    : sets;

  const setsWonA = updatedSets.filter(s => s.winnerId === teamA.id).length;
  const setsWonB = updatedSets.filter(s => s.winnerId === teamB.id).length;

  const matchWinner = checkMatchWin(setsWonA, setsWonB, match.matchType);
  const matchWinnerId = matchWinner
    ? matchWinner === 'A'
      ? teamA.id
      : teamB.id
    : null;

  let status: MatchStatus;
  if (activeTimeout) {
    status = 'timeout';
  } else if (matchWinnerId) {
    status = 'match-won';
  } else if (setWinnerId) {
    status = 'set-won';
  } else if (rallies.length === 0 && match.status === 'first-server') {
    status = 'first-server';
  } else {
    status = 'live';
  }

  const goldenPoint = isGoldenPointMode(scoreA, scoreB, matchRules);

  const sideChangeDue = deriveSideChange(
    scoreA,
    scoreB,
    matchRules,
    currentSetNumber,
    currentSetRecord?.sideChangeShown ?? false,
  );

  const rotationA = rebuildRotation(
    teamA.id,
    rallies,
    servingTeamId === teamA.id ? (currentServerId || null) : null,
  );
  const rotationB = rebuildRotation(
    teamB.id,
    rallies,
    servingTeamId === teamB.id ? (currentServerId || null) : null,
  );

  const currentSet: ComputedSetState | null = currentSetRecord
    ? {
        setNumber: currentSetNumber,
        setId: currentSetRecord.id,
        scoreA,
        scoreB,
        servingTeamId,
        currentServerId,
        goldenPoint,
        setWinnerId,
        rallyCount: rallies.length,
        endedAt: setWinnerId ? currentSetRecord.endedAt ?? new Date().toISOString() : null,
        rallies: [...rallies].sort((a, b) => b.rallyNumber - a.rallyNumber),
      }
    : null;

  return {
    status,
    currentSetNumber,
    currentSet,
    setsWonA,
    setsWonB,
    matchWinnerId,
    teamA: buildTeamState(teamA, setsWonA, rotationA),
    teamB: buildTeamState(teamB, setsWonB, rotationB),
    sets: updatedSets,
    timeoutCountA: countTimeouts(timeouts, teamA.id),
    timeoutCountB: countTimeouts(timeouts, teamB.id),
    activeTimeout,
    sideChangeDue,
    matchRules,
  };
}

export function computedToMatchState(
  computed: ComputedMatchState,
  match: Match,
  tournamentRules: TournamentRules | null,
): MatchState {
  const currentSet: SetState | null = computed.currentSet
    ? {
        scoreA: computed.currentSet.scoreA,
        scoreB: computed.currentSet.scoreB,
        rallies: computed.currentSet.rallies,
        servingTeamId: computed.currentSet.servingTeamId,
        currentServerId: computed.currentSet.currentServerId,
        goldenPoint: computed.currentSet.goldenPoint,
      }
    : null;

  return {
    match: {
      ...match,
      status: computed.status,
      winnerId: computed.matchWinnerId,
    },
    teamA: computed.teamA,
    teamB: computed.teamB,
    currentSetNumber: computed.currentSetNumber,
    currentSet,
    status: computed.status,
    sets: computed.sets,
    tournamentRules: tournamentRules ?? undefined,
  };
}
